<script>
    /*
        ResourceExplorerPage.svelte

        This page now has "fixed" top and bottom sections.
        The bottom section contains left-side, middle and right-side sections.

    */

	import { onMount } from 'svelte';
    import { push, replace } from 'svelte-spa-router';
    import { fly } from 'svelte/transition';

	import { masterSearch } from '../../common/master-objects.js';
	import { runPublicPostalSearch, runPublicFilterSearch } from '../../common/public-search-utils.js';
	import { runPublicDemographicsSearch, runPublicSortSearch } from '../../common/public-search-utils.js';
    import { isShowingZipError } from '../../stores/ScreenSettingsStore'
    
    import { setErrorAlert, setWarningAlert } from '../../stores/AlertsStore';
    import { DEMOGRAPHICS, OPEN_ELIG_2022 } from "../../common/taxonomy-classes.js";
    import {asyncValidatePostalCode, validatePostalCode} from "../../common/utils.js";
    
    // we need these as stores as they are also used on the LandingPage
    import { isShowingSearchFilters } from '../../stores/ScreenSettingsStore';
	import { isRunningSearch, isWindowNarrowView, currentListItemId } from '../../stores/ScreenSettingsStore.js';
    import { AccountStore } from "../../common/account.ts";
    import { currentMenuNode } from '../../stores/ResourceMenuStore.js';

    // TCARE components
    import PageAlert from '../../components/common/PageAlert.svelte';
    // import CircularSpinner from '../../components/common/CircularSpinner.svelte';
    import PublicSearchPanel from '../../components/searches/PublicSearchPanel.svelte';

    import VerticalResourceMenu from '../../components/resource-menu/VerticalResourceMenu.svelte';
    import VerticalCategoryPicker from '../../components/resource-menu/VerticalCategoryPicker.svelte';

    import ExplorerServiceList from '../../components/lists/ExplorerServiceList.svelte';
    import FiltersButton from '../../components/filters-menu/FiltersButton.svelte';
    import FiltersMenu from '../../components/filters-menu/FiltersMenu.svelte';

    // import Header from '../../components/header/Header.svelte';
    import SortComponent from '../../components/sort-component/SortComponent.svelte';
    import ResourceSavedPopup from '../../components/popups/ResourceSavedPopup.svelte';

    // Vito code
    import { DEMO_POSTAL_WARNING_MSG, isPostalLockedWithWrongPostal } from "../../../demo.js";
    // import { ifPostalLockedIsCorrectPostal } from "../../../demo.js";

    const SELECT_CATEGORY_MESSAGE = 'Select from these categories to show services in';
    const RUNNING_SEARCH_MESSAGE = 'Searching for services in';
    const NEW_SEARCH_MESSAGE = 'Please start a new search by entering a ZIP Code above';
    
    const POSTAL_WARNING_MSG = 'Please enter a valid ZIP Code.';
    const SEARCH_ERROR_MSG = 'An error occurred during your search.';

    const PATH_SVG = "img/butterfly_2.svg";

    $: staticPostalCode = '';       // prevent postal from changing when user types new one
    $: isShowingPopup = false;

    onMount( async () => {
        // console.log('');
        // console.log('ResourceExplorerPage onMount');
        // console.log('$masterSearch');
        // console.log($masterSearch);

        if (!$AccountStore?.isLoggedIn) {
            replace('/landing');                                  // and login
            return;
        }

        if ($masterSearch.postalResults.length === 0) {
            push('/postal-results');
        }

        $isShowingSearchFilters = true;                             // in store
        staticPostalCode = $masterSearch.location.postalCode;       // when returning from another page

        // scroll to opened item, if exists - use store?
        // must set item CSS: scroll-margin-top: 55px;
        scrollTo(0, 0);
        if ($currentListItemId !== '') {
            scrollToMarker($currentListItemId);
            $currentListItemId = '';                // clear it
        }
    })

    // must set item CSS: scroll-margin-top: 55px;
    const scrollToMarker = (id) => {
        let marker = document.getElementById(id);
        if (marker) {
            marker.scrollIntoView({behavior: 'smooth'});
        }
    }

    const onShowFiltersClick = () => {
        $isShowingSearchFilters = true;
    }

    // returning true will force a FILTER search
    const searchHasFilters = () => {

        // first check the free text field
        if ($masterSearch.text && $masterSearch.text.trim() !== '') {
            return true;
        }

        // check for any keywords (or demographics) selected
        if ($masterSearch.getSelectedNodes().length > 0) {
            return true;
        }

        // all other conditions will force a new POSTAL search
        return false;
    }

    const onFiltersButtonClick = () => {
        $isShowingSearchFilters = !$isShowingSearchFilters;
    }
    
    // called from the SortComponent - do not alter the FiltersMenu!
    const onSort = (sortField, isAscending) => {
        runResourcePageSortSearch(sortField, isAscending);
    }        

    // pass to the list items
    const onShowPopup = () => {
        isShowingPopup = true;
    }
    
    const _onPopupCancel = () => {
        isShowingPopup = false;
    }
    
    const _onPopupConfirm = () => {
        isShowingPopup = false;
        push('/my-resources');
    }


    ///////////////////////////////   POSTAL, FILTER AND DEMOGRAPHIC SEARCH FUNCTIONS   ////////////////////////////////

    // this is the main ZIP Code search - no filters
    // fills the search.postalResults[] array
    // this search also redraws the ResourceMenu
    const runResourcePagePostalSearch = async () => {
        // console.log('ResourceExplorerPage runResourcePagePostalSearch: ' + $masterSearch.location.postalCode);

        if (!validatePostalCode($masterSearch.location.postalCode)) {
            $isShowingZipError = true
            return false;
        }
        if(isPostalLockedWithWrongPostal($masterSearch.location.postalCode)){
            $isShowingZipError = true
            return false;
        }

        staticPostalCode = $masterSearch.location.postalCode; 
        $masterSearch.postalResults = [];
        $masterSearch.clearTaxonomies();
        $masterSearch.text = '';                                            // or null?
        $masterSearch = $masterSearch;

        $isRunningSearch = true;
        const results = await runPublicPostalSearch(masterSearch);          // in public-search-utils.js
        $isRunningSearch = false;
        $masterSearch = $masterSearch;

        if (results.errors) {
            $isShowingZipError = true
            $masterSearch.postalResults = [];                               // avoid error
            return false;
        } else {
            $isShowingZipError = false
            staticPostalCode = $masterSearch.location.postalCode;           // cache this for static UI display
            push('/postal-results');                                        // or stay on same page?
            return true;
        }
    }

    // this returns a different set of results - the postalResults are retained for the menu
    // fills the search.filterResults[] array
    // this search DOES NOT redraw the ResourceMenu
    // this search DOES redraw the filters checkboxes
    const runResourcePageFilterSearch = async () => {
        // console.log('ResourceExplorerPage runResourcePageFilterSearch: ');

        if (!validatePostalCode($masterSearch.location.postalCode)) {
            $isShowingZipError = true
            return;
        }

        if (!searchHasFilters()) {
            runResourcePagePostalSearch();
            return;
        }

        $masterSearch.filterResults = [];

        $isRunningSearch = true;
        const results = await runPublicFilterSearch(masterSearch);       // in public-search-utils.js
        $isRunningSearch = false;

        if (results.errors) {
            $isShowingZipError = true
            $masterSearch.filterResults = [];                               // avoid null error downstream
            return false;
        } else {
            $isShowingZipError = false
            // console.log('filterResults:');
            // console.log($masterSearch.filterResults);
            return true;
        }
    }

    // this search DOES NOT redraw the filters checkboxes
    const runResourcePageDemographicsSearch = async () => {
        // console.log('ResourceExplorerPage runResourcePageDemographicsSearch');

        // $isShowingSearchFilters = false;

        if (!validatePostalCode($masterSearch.location.postalCode)) {
            $isShowingZipError = true
            return;
        }

        if (!searchHasFilters()) {
            runResourcePagePostalSearch();
            return;
        }

        $masterSearch.filterResults = [];

        $isRunningSearch = true;
        const results = await runPublicDemographicsSearch(masterSearch);     // in public-search-utils.js
        $isRunningSearch = false;

        if (results.errors) {
            $isShowingZipError = true
            $masterSearch.filterResults = [];                       // avoid error
            return false;
        } else {
            $isShowingZipError = false
            return true;
        }
    }

    // new 11/24 - this search DOES NOT redraw the filters checkboxes
    // note that sortField is not the same as propName ("service-name" -> "name")
    const runResourcePageSortSearch = async (sortField, isAscending) => {

        const isValidZipCode = await asyncValidatePostalCode($masterSearch.location.postalCode)
        if (!isValidZipCode) {
            $isShowingZipError = true
            return false
        }

        if (!searchHasFilters()) {
            runResourcePagePostalSearch();
            return;
        }

        $masterSearch.filterResults = [];

        $isRunningSearch = true;
        const results = await runPublicSortSearch(masterSearch, sortField, isAscending);     // in public-search-utils.js
        $isRunningSearch = false;

        if (results.errors) {
            $isShowingZipError = true
            $masterSearch.filterResults = [];                       // avoid error
            return false;
        } else {
            $isShowingZipError = false
            return true;
        }
    }


</script>

<style>

    .explorer-page {
        position: relative;
        width: 100%;
    }

    /* just beneath Header - contains PublicSearchBar */
    .explorer-page .fixed-top {
        z-index: 0;
        position: relative;
        top: 0px;
        left: 0%;
        width: 100%;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        /* border: 1px solid red; */
    }

    /* contains CategoryMenu, list and FiltersMenu */
    .explorer-page .fixed-bottom {
        position: relative;
        width: 100%;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        /* border: 1px solid blue; */
    }

    /* nudge this up to be even with top bar */
    .explorer-page .left {
        position: relative;
        width: 320px;
        margin-top: -20px;
        height: calc(100vh - 225px);
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        /* border: 1px solid red; */
    }

    .explorer-page .left {
        -ms-overflow-style: none;  /* Internet Explorer 10+ */
        scrollbar-width: none;  /* Firefox */
    }

    .explorer-page .left::-webkit-scrollbar { 
        display: none;  /* Safari and Chrome */
    }    


    .explorer-page .middle {
        position: relative;
        width: 700px;
        height: calc(100vh - 225px);
        overflow-x: hidden;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        /* border: 1px solid green; */
    }

    .explorer-page .middle {
        -ms-overflow-style: none;  /* Internet Explorer 10+ */
        scrollbar-width: none;  /* Firefox */
    }

    .explorer-page .middle::-webkit-scrollbar { 
        display: none;  /* Safari and Chrome */
    }    

    .explorer-page .right {
        position: relative;
        width: 250px;
        height: calc(100vh - 225px);
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        /* border: 1px solid blue; */
    }

    .explorer-page .right {
        -ms-overflow-style: none;  /* Internet Explorer 10+ */
        scrollbar-width: none;  /* Firefox */
    }

    .explorer-page .right::-webkit-scrollbar { 
        display: none;  /* Safari and Chrome */
    }    


    
    /* fixed-top elements */
    .explorer-page .row-0 {
        width: 100%;
        /* border: 1px solid green; */
    }

    .explorer-page .row-1 {
        width: 100%;
        height: 35px;
        margin-bottom: 5px;
        display: flex;
        align-items: center;
    }

    .col-0 { position: absolute; left: 0px; }
    .col-1 { position: absolute; left: 355px; }
    .col-2 { position: absolute; left: 660px; }
    .col-3 { position: absolute; left: 1050px; }

    /* fixed-left elements */



    /* fixed-right elements */


    /* new Explorer Page layout and display logic 11/16/22 */
    .message-row {
        display: flex;
        align-items: flex-start;
        justify-content: center;
    }

    .select-cat {
        width: 325px;
        margin-top: 20px;
        font-size: 36px;
        font-weight: 400;
        line-height: 44px;
        text-align: right;
        color: var(--tcui-secondary);
        /* border: 1px solid white; */
    }

    .select-cat span {
        color: var(--tcui-white);
    }

    .new-search {
        margin: auto;
        margin-top: 50px;
        font-size: 36px;
        font-weight: 400;
        line-height: 40px;
        text-align: center;
        color: var(--tcui-secondary);
    }

    .new-search span {
        color: var(--tcui-white);
    }


    .w-100 {
        width: 100%;
    }

    .showing-n-results {
        font-size: 13px;
        font-weight: 500;
        color: #A075A5;
    }

    .mobile-top {
        padding: 10px 0px;
        margin-bottom: 10px;
        text-align: center;
        font-size: 16px;
        width: 100%;
        color: white;
        background-color: var(--tcui-primary);
        border-bottom: 1px solid purple;
    }


    @keyframes fade-in {
        from {opacity: 0}
        to {opacity: 1}
    }

    @keyframes fade-in-from-right {
        from {opacity: 0; left: 200px;}
        to {opacity: 1; left: 0px;}
    }

    

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>





<!-- no slide in animation for this page -->
<div class="page-content explorer-page">

    <!-- <Header user={$AccountStore} /> -->
    <PageAlert />

    <!-- MOBILE VIEW -->
    {#if $isWindowNarrowView}
        <div class="mobile-top">Mobile View</div>
        <div class="mobile-top">Mobile Explorer Page TBA</div>
    {/if}

    <!-- NOT MOBILE VIEW -->
    {#if !$isWindowNarrowView}
        <!-- always show the top search bar even when searching -->    
        <div class="fixed-top">
            <div class="flex-row row-0">
                <PublicSearchPanel store={masterSearch} 
                    onPostalSearch={runResourcePagePostalSearch} 
                    onFilterSearch={runResourcePageFilterSearch}     
                    onDemographicsSearch={runResourcePageDemographicsSearch}     
                    {onShowFiltersClick}
                    isShowingFreeText={true}
                                   {isShowingZipError}
                />
            </div>   
        </div>

        {#if $masterSearch.postalResults.length > 0}
            <div class="flex-row row-1">
                <!-- <div class="col-0 showing-n-results">Categories in {staticPostalCode}</div> -->
                <div class="col-1 showing-n-results">
                    {#if $masterSearch.filterResults.length > 0}
                        Showing {$masterSearch.filterResults.length} results in {$currentMenuNode.label}
                    {/if}
                </div>
                <div class="col-2">
                    <SortComponent {onSort} />
                </div>
                <div class="col-3">
                    <!-- TODO: FiltersMenu -->
                    <FiltersButton onClick={onFiltersButtonClick} isSelected={$isShowingSearchFilters} />
                </div>
            </div>

            <div class="fixed-bottom">
                <div class="left">
                    <VerticalResourceMenu 
                        searchStore={masterSearch} 
                        taxonomyName={OPEN_ELIG_2022} 
                        onSearch={runResourcePageFilterSearch}
                        isAnimated={false}
                    />
                    <VerticalCategoryPicker 
                        searchStore={masterSearch} 
                        onSearch={runResourcePageFilterSearch}
                    />
                </div>

                <div class="middle">
                    {#if $currentMenuNode}
                        <ExplorerServiceList searchStore={masterSearch} {onShowPopup} />
                        <!-- <ExplorerServiceList searchStore={masterSearch} {sortProp} {isAscending} /> -->
                    {/if}
                </div>
        
                <div class="right">
                    <FiltersMenu 
                        searchStore={masterSearch} 
                        taxonomyName={DEMOGRAPHICS} 
                        onSearch={runResourcePageDemographicsSearch}
                        isOpen={$isShowingSearchFilters}
                    />
                </div>
            </div>
        {/if}
    {/if}

    <!--
    {#if $isRunningSearch}
        <div class="search-progress">
            <CircularSpinner delay={500} label={'Searching services...'}  />
        </div>
    {/if}
    -->

</div>


<ResourceSavedPopup 
    isShowing={isShowingPopup} 
    onCancel={_onPopupCancel} 
    onConfirm={_onPopupConfirm} 
/>
